<script setup lang="ts">
import { computed, ref } from "vue";
import { formatVolume } from "../format";
import type { Overview } from "../types/api";

const props = defineProps<{ data: Overview; now: number }>();
const range = ref("today");

// Fallback for an API that predates traffic_source_catalog.
const fallbackNames: Record<string, string> = {
  huggingface: "Hugging Face",
  github: "GitHub",
  apple: "Apple",
  google: "Google / YouTube",
  microsoft: "Microsoft",
  openai: "OpenAI / ChatGPT",
  anthropic: "Anthropic / Claude",
  steam: "Steam",
  nvidia: "NVIDIA",
  jetbrains: "JetBrains",
  hysteria: "Hysteria（全部目标）",
  other: "其他 / 未识别"
};
const catalog = computed(() => props.data.traffic_source_catalog ?? []);
const names = computed<Record<string, string>>(() => ({
  ...fallbackNames,
  ...Object.fromEntries(catalog.value.map((item) => [item.source, item.label]))
}));
const dates = computed(() =>
  [...new Set((props.data.traffic_sources ?? []).map((row) => row.date))].sort().reverse()
);

const daysAgo = (days: number) => new Date(props.now - days * 86_400_000).toISOString().slice(0, 10);
function inRange(date: string): boolean {
  if (range.value === "today") return date === daysAgo(0);
  if (range.value === "7d") return date >= daysAgo(6);
  return range.value === "all" || date === range.value;
}

const rows = computed(() => {
  const grouped = new Map<string, { source: string; upload: number; download: number; total: number }>();
  for (const row of props.data.traffic_sources ?? []) {
    if (!inRange(row.date)) continue;
    const entry = grouped.get(row.source) ?? { source: row.source, upload: 0, download: 0, total: 0 };
    entry.upload += row.upload_bytes;
    entry.download += row.download_bytes;
    entry.total += row.total_bytes;
    grouped.set(row.source, entry);
  }
  return [...grouped.values()].sort((a, b) => b.total - a.total);
});
/** Categories that saw no traffic in the range are counted, not listed. */
const activeRows = computed(() => rows.value.filter((row) => row.total > 0 || row.source === "hysteria"));
const idleCount = computed(() => rows.value.length - activeRows.value.length);
const total = computed(() => rows.value.reduce((sum, row) => sum + row.total, 0));
const share = (value: number) => (total.value ? (value / total.value) * 100 : 0);
const otherShare = computed(() => share(rows.value
  .filter((row) => row.source === "other" || row.source === "hysteria")
  .reduce((sum, row) => sum + row.total, 0)));
const updated = computed(() => Math.max(0, ...(props.data.traffic_sources ?? []).map((row) => row.updated_at)));
const updatedText = computed(() => new Date(updated.value * 1000).toISOString().replace("T", " ").slice(0, 16));
const domainText = (domains: string[]) =>
  domains.map((domain) => (domain.startsWith("full:") ? `${domain.slice(5)}（仅此主机）` : domain)).join("、");
</script>

<template>
  <section class="source-section" aria-labelledby="source-heading">
    <div class="traffic-head">
      <div>
        <h2 id="source-heading">流量来源排行</h2>
        <p>Xray 按目标服务 · Hysteria 单独汇总 · UTC</p>
      </div>
      <div class="traffic-controls">
        <label>
          日期
          <select v-model="range">
            <option value="today">今日</option>
            <option value="7d">最近 7 天</option>
            <option value="all">最近 30 天</option>
            <option v-for="date in dates" :key="date" :value="date">{{ date }}</option>
          </select>
        </label>
      </div>
    </div>

    <p v-if="!activeRows.length" class="traffic-note">所选日期暂无来源记录。</p>
    <template v-else>
      <p class="source-total">
        <span>所选时段合计</span>
        <strong>{{ formatVolume(total) }}</strong>
        <span>已识别目标服务 {{ (100 - otherShare).toFixed(1) }}%</span>
      </p>
      <div class="traffic-table source-table">
        <table>
          <thead>
            <tr>
              <th scope="col">来源</th>
              <th scope="col" class="optional">下载</th>
              <th scope="col" class="optional">上传</th>
              <th scope="col">合计</th>
              <th scope="col">占比</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in activeRows" :key="row.source">
              <th scope="row">{{ names[row.source] || row.source }}</th>
              <td class="optional">{{ formatVolume(row.download) }}</td>
              <td class="optional">{{ formatVolume(row.upload) }}</td>
              <td><strong>{{ formatVolume(row.total) }}</strong></td>
              <td>
                <span class="share">
                  <span class="share__bar" :class="{ 'share__bar--other': row.source === 'other' }" aria-hidden="true">
                    <i :style="{ width: `${share(row.total)}%` }"></i>
                  </span>
                  <span class="share__value">{{ share(row.total).toFixed(1) }}%</span>
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <p v-if="updated" class="traffic-note">
      <template v-if="idleCount && activeRows.length">另有 {{ idleCount }} 个分类在这段时间没有流量。</template>
      更新于 {{ updatedText }} UTC{{ now / 1000 - updated > 180 ? "（采集延迟）" : "" }}
    </p>
    <details>
      <summary>统计口径与说明</summary>
      <p>
        从启用统计起累计，历史流量无法追溯。Hysteria 单列全部目标的流量，暂不按网站或客户端 IP 拆分。下载指目标服务 → 用户，上传指用户 → 目标服务。仅统计代理出口，不能与网卡收发合计直接相加。无法识别的域名、共享
        CDN 和直连 IP 可能归入其他。约每分钟采样，跨日流量计入采样日。分类新增或扩充后才开始单独计数，此前的流量仍保留在“其他”中。
      </p>
    </details>
    <details v-if="catalog.length">
      <summary>分类覆盖的域名</summary>
      <dl class="source-catalog">
        <template v-for="item in catalog" :key="item.source">
          <dt>{{ item.label }}</dt>
          <dd>{{ item.source === "hysteria" ? "Hysteria 全部目标，未按域名分类" : item.domains.length ? `${domainText(item.domains)} 及其子域名` : "以上规则均未匹配的目标，包括直连 IP" }}</dd>
        </template>
      </dl>
    </details>
  </section>
</template>

<style scoped>
.source-section { min-width: 0; }
.source-total { display: flex; flex-wrap: wrap; gap: 0.25rem 0.75rem; align-items: baseline; margin-bottom: 1.25rem; color: var(--ink-3); font-size: 0.8125rem; }
.source-total strong { color: var(--ink); font-size: 1.5rem; font-weight: 500; letter-spacing: -0.02em; }

.share { display: inline-flex; gap: 0.6rem; align-items: center; justify-content: flex-end; }
/* One colour for every source: bar length already carries the ranking. */
.share__bar { width: clamp(3rem, 12vw, 7rem); height: 0.375rem; border-radius: 999px; background: var(--accent-faint); overflow: hidden; }
.share__bar i { display: block; height: 100%; border-radius: inherit; background: var(--accent); }
.share__bar--other { background: var(--wash); }
.share__bar--other i { background: var(--idle); }
.share__value { min-width: 3.2em; color: var(--ink-2); }

.source-catalog { display: grid; grid-template-columns: max-content 1fr; gap: 0.35rem 1rem; margin: 0.5rem 0 0; }
.source-catalog dt { color: var(--ink); }
.source-catalog dd { overflow-wrap: anywhere; }

@media (max-width: 640px) {
  .source-table .optional { display: none; }
  .source-table tbody th { white-space: normal; }
  .source-catalog { grid-template-columns: 1fr; }
  .source-catalog dd { margin-bottom: 0.4rem; }
}
</style>
